import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { contesto, useTraduzioni } from '../../i18n';
import { browserDiIPhone, piattaforma } from '../../utils/installa';
import {
  DisegnoAggiungi, DisegnoConferma, DisegnoConfermaAndroid, DisegnoCondividi, DisegnoInstallaApp, DisegnoMenu, DisegnoSafari,
} from './DisegniInstalla';
import './installa.css';

// La guida a schermo intero: i passi per mettere MK Client sulla schermata Home, disegnati (DisegniInstalla.jsx)
// e scritti nella lingua del cliente.
//
// Tre situazioni diverse, tre guide diverse:
// - iPhone in Safari  → Condividi › «Aggiungi alla schermata Home» › «Aggiungi»;
// - iPhone in Chrome/Firefox → non si può, e si dice: solo Safari ha quel menu. Si offre di copiare il link
//   (funziona sempre) e, dove il sistema lo permette, di aprirlo direttamente in Safari;
// - Android → i tre puntini › «Installa app» › «Installa». Ci si arriva solo quando Chrome non ha offerto la
//   finestra vera (utils/installa.js): se l'ha offerta, il cliente non vede mai questa guida.
//
// La lingua: dentro l'app è quella del cliente (contesto di LayoutCliente). Sulla pagina pubblica /installa,
// dove nessuno è collegato, arriva da fuori con la prop `lingua` (il link di WhatsApp la porta con sé).

const PASSI_IPHONE = [
  { chiave: 'passo1', Disegno: DisegnoCondividi },
  { chiave: 'passo2', Disegno: DisegnoAggiungi },
  { chiave: 'passo3', Disegno: DisegnoConferma },
];

const PASSI_ANDROID = [
  { chiave: 'android1', Disegno: DisegnoMenu },
  { chiave: 'android2', Disegno: DisegnoInstallaApp },
  { chiave: 'android3', Disegno: DisegnoConfermaAndroid },
];

// L'indirizzo da copiare: la pagina della guida, con la lingua dentro, così chi la riapre in Safari la ritrova
// nella sua lingua.
export const indirizzoGuida = (lingua) => {
  const base = typeof window !== 'undefined' && window.location ? window.location.origin : 'https://mkclient.it';
  return `${base}/installa${lingua && lingua !== 'it' ? `?lingua=${lingua}` : ''}`;
};

// Su iOS `x-safari-https://…` apre Safari da un'altra app. Non tutti i browser lo rispettano: se non succede
// niente resta «Copia il link», che invece funziona sempre. Per questo il primo bottone è quello.
const apriInSafari = (indirizzo) => indirizzo.replace(/^http/, 'x-safari-http');

const Passo = ({ numero, titolo, nota, Disegno }) => (
  <div className="passo" data-testid={`passo-${numero}`}>
    <Disegno titolo={titolo} />
    <span className="numero" aria-hidden="true">{numero}</span>
    <div className="testo">
      <b>{titolo}</b>
      {nota && <span>{nota}</span>}
    </div>
  </div>
);

const GuidaInstalla = ({ onChiudi, lingua = null, comePagina = false }) => {
  const daContesto = useTraduzioni();
  const { t, l } = lingua ? contesto(lingua) : daContesto;
  const [copiato, setCopiato] = useState(false);
  const chiusura = useRef(null);

  const sistema = piattaforma();
  // Su un computer (o un telefono che non riconosciamo) si mostrano i passi dell'iPhone: sono i più richiesti,
  // e chi guarda da un computer sta quasi sempre aiutando qualcuno con il telefono in mano.
  const iPhone = sistema !== 'android';
  const daAltroBrowser = sistema === 'iphone' && browserDiIPhone() === 'altro';
  const passi = iPhone ? PASSI_IPHONE : PASSI_ANDROID;
  const indirizzo = indirizzoGuida(l);

  // Esc chiude, e mentre la guida è aperta la pagina sotto non scorre
  useEffect(() => {
    if (comePagina) return undefined;
    const tasto = (e) => { if (e.key === 'Escape') onChiudi?.(); };
    document.addEventListener('keydown', tasto);
    const prima = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    if (chiusura.current) chiusura.current.focus();
    return () => {
      document.removeEventListener('keydown', tasto);
      document.body.style.overflow = prima;
    };
  }, [onChiudi, comePagina]);

  const copia = async () => {
    try {
      await navigator.clipboard.writeText(indirizzo);
      setCopiato(true);
      setTimeout(() => setCopiato(false), 2500);
    } catch {
      // niente permesso per gli appunti (succede): l'indirizzo resta scritto a video, si copia a mano
      setCopiato(false);
    }
  };

  const dentro = (
    <div className="dentro">
      {/* Dentro la pagina /installa il titolo ce l'ha già la pagina: qui ne verrebbe un secondo, quasi uguale */}
      {!comePagina && (
        <div className="testa">
          <div>
            <h2 id="guida-installa-titolo">{t('installa.guida.titolo')}</h2>
            <p>{t('installa.guida.sotto')}</p>
          </div>
          <button type="button" className="x" ref={chiusura} onClick={onChiudi}
            aria-label={t('installa.guida.chiudi')} data-testid="guida-chiudi">✕</button>
        </div>
      )}

      {/* iPhone fuori da Safari: i passi non servirebbero a niente, perché quel menu lì non c'è */}
      {daAltroBrowser && (
        <div className="avviso" data-testid="guida-safari">
          <DisegnoSafari titolo="Safari" />
          <b>{t('installa.guida.safari_titolo')}</b>
          <p>{t('installa.guida.safari_testo')}</p>
          <div className="bottoni">
            <button type="button" className="b p" onClick={copia} data-testid="guida-copia">
              {copiato ? t('installa.guida.copiato') : t('installa.guida.copia')}
            </button>
            <a className="b" href={apriInSafari(indirizzo)} data-testid="guida-apri-safari">{t('installa.guida.apri_safari')}</a>
          </div>
        </div>
      )}

      {passi.map(({ chiave, Disegno }, i) => (
        <Passo key={chiave} numero={i + 1} titolo={t(`installa.guida.${chiave}`)}
          nota={t(`installa.guida.${chiave}_nota`)} Disegno={Disegno} />
      ))}

      <p className="chiusura" data-testid="guida-fatto">{t('installa.guida.fatto')}</p>

      {!comePagina && (
        <div className="bottoni">
          <button type="button" className="b" onClick={onChiudi}>{t('installa.guida.chiudi')}</button>
        </div>
      )}
    </div>
  );

  if (comePagina) return <div className="mk-installa" style={{ position: 'static' }} data-testid="guida-installa" data-sistema={sistema}>{dentro}</div>;

  return createPortal(
    <div className="mk-installa" role="dialog" aria-modal="true" aria-labelledby="guida-installa-titolo"
      data-testid="guida-installa" data-sistema={sistema}>
      {dentro}
    </div>,
    document.body,
  );
};

export default GuidaInstalla;
